<template>
  <div @click.stop :class="{'hidden': !showTestControls}" class="test-controls">
    <div class="buttons has-addons is-white">
      <div @click="slideshow.previousStep()" class="button prev-step"><i class="fas fa-chevron-left" /></div>
      <div @click="addTestPlayers()" :class="{'is-loading': loading }" :disabled="loading" class="add-players button is-dark-transparent">
        Add {{ playerCount }} Players
      </div>
      <div @click="addNumberOfPlayers(100)" :class="{'is-loading': loading }" :disabled="loading" class="button is-dark-transparent">
        +100 Players
      </div>
      <div @click="addTestPlayer()" :class="{'is-loading': loading }" :disabled="loading" class="button is-dark-transparent">
        +1
      </div>
      <div @click="sendResponses" :class="{'is-loading': loading }" :disabled="loading" class="send-responses button is-dark-transparent">
        Send Responses
      </div>
      <div @click="slideshow.nextStep()" class="button next-step"><i class="fas fa-chevron-right" /></div>
      <div @click="toggleTestControls()" class="button toggle"><i class="fas fa-ellipsis-v" /></div>
    </div>
    <div v-if="!showTestControls" @click="toggleTestControls()" class="button toggle"><i class="fas fa-ellipsis-v" /></div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import shuffle from 'lodash.shuffle'
import { names } from '~/common/mocks/names.js'
import { phrases } from '~/common/mocks/phrases.js'
import { features } from '~/common/mocks/features.js'

import addNewPlayer from '~/queries/addNewPlayer'
import AvatarControls from '~/components/mixins/AvatarControls'

export default {
  mixins: [AvatarControls],
  props: ['event'],
  data () {
    return {
      testPlayers: [],
      playerCount: 10,
      loading: false
    }
  },
  computed: {
    ...mapState([
      'activeEvent',
      'showTestControls'
    ]),
    slideshow () {
      return this.$parent
    },
    slide () {
      return this.$parent.currentSlideTemplate
    },
    slideType () {
      return this.$parent.currentSlide.template // is the name of the slide template
    }
  },
  methods: {
    toggleTestControls () {
      this.$store.commit('toggleShowTestControls')
    },
    getRandomInt (min, max) {
      min = Math.ceil(min)
      max = Math.floor(max)
      return Math.floor(Math.random() * (max - min + 1)) + min
    },
    getRandomName () {
      return shuffle(names)[0]
    },
    getRandomAvatar () {
      return shuffle(this.allEmoji)[0]
    },
    getRandomText () {
      return shuffle(phrases)[0]
    },
    getRandomAnimal () {
      const number = this.getRandomInt(0, 150)
      const randomAnimals = shuffle(features)
      if (number < 150) {
        const weightedNumber = shuffle([1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 5, 6, 7, 8, 9, 10, 11, 12])[0]
        return features[weightedNumber - 1]
      } else {
        return randomAnimals[number]
      }
    },
    async addNumberOfPlayers (num = 5) {
      for (let i = 0; i < num; i++) {
        await this.addTestPlayer()
      }
    },
    async addTestPlayers () {
      console.log('adding test players...')
      if (this.testPlayers.length < this.playerCount && this.event.participants.length < this.playerCount) {
        await this.addTestPlayer()
        window.setTimeout(() => {
          this.addTestPlayers()
        }, 30)
      }
    },
    async addTestPlayer () {
      this.loading = true
      let name = this.showName ? this.getRandomName() : this.getAnonymousName()
      if (this.testPlayers.length === 0) { name = 'Always Right' }
      if (this.testPlayers.length === 1) { name = 'Wrong Every Time' }

      const avatar = this.showAvatar ? this.getRandomAvatar() : null
      // eslint-disable-next-line camelcase
      const { data: { insert_participants } } = await this.$apollo.mutate({
        mutation: addNewPlayer,
        variables: {
          eventId: this.event.id,
          name,
          avatar
        }
      })

      const playerId = insert_participants.returning[0].id

      const player = {
        id: playerId,
        name,
        avatar
      }

      this.testPlayers.push(player)
      console.log('test player added')
      this.loading = false
    },
    async sendResponses () {
      console.log('sending all responses')
      const players = shuffle(this.event.participants)

      for (let i = 0; i < players.length; i++) {
        const start = Date.now()
        if (this.slideType === 'ratingSlide') { await this.submitRating(players[i]) }
        if (this.slideType === 'multipleChoice') { await this.submitMultiChoice(players[i]) }
        if (this.slideType === 'pollSlide') { await this.submitMultiChoice(players[i]) }
        if (this.slideType === 'specificTextAnswer') { await this.submitSpecificText(players[i]) }
        if (this.slideType === 'userVote') { await this.submitUserVoteText(players[i]) }
        if (this.slideType === 'wordCloud') { await this.submitWordCloudText(players[i]) }
        const end = Date.now()

        this.log('response time: ' + (end - start) + 'ms')
      }

      if (this.slideType === 'userVote') {
        console.log('setting timeout for user voting')
        setTimeout(() => {
          this.submitUserVotes(players)
        }, 2000)
      }
    },
    /*
    * Responses for Rating Slide
    */
    async submitRating (player) {
      console.log('submitting multiple choice for player: ' + player.name)
      if (player.name === 'Always Right') { await this.submitBestRating(player); return }
      if (player.name === 'Wrong Every Time') { await this.submitWorstRating(player); return }
      await this.submitRandomRating(player)
    },
    async submitBestRating (player) {
      this.$store.commit('setActivePlayer', player)
      const scale = this.slide.currentScale
      const best = scale.length
      await this.slide.respondWithNumber(best)
    },
    async submitWorstRating (player) {
      this.$store.commit('setActivePlayer', player)
      await this.slide.respondWithNumber(1)
    },
    async submitRandomRating (player) {
      this.$store.commit('setActivePlayer', player)
      const scale = this.slide.currentScale
      const max = scale.length
      const random = this.getRandomInt(1, max)
      await this.slide.respondWithNumber(random)
    },
    /*
    * Responses for MultiChoice Slide
    */
    async submitMultiChoice (player) {
      if (player.name === 'Always Right') { await this.submitSpecificChoice(player, true); return }
      if (player.name === 'Wrong Every Time') { await this.submitSpecificChoice(player, false); return }
      await this.submitRandomChoice(player)
    },
    async submitSpecificChoice (player, isCorrect) {
      this.$store.commit('setActivePlayer', player)
      const answers = this.slide.answers
      const key = answers.findIndex(a => a.isCorrect === isCorrect)

      if (key > -1) {
        await this.slide.respondMultipleChoice(answers, key)
      } else {
        await this.submitRandomChoice(player)
      }
    },
    async submitRandomChoice (player) {
      this.$store.commit('setActivePlayer', player)
      const answers = this.slide.answers
      const max = answers.length - 1
      const random = this.getRandomInt(0, max)
      await this.slide.respondMultipleChoice(answers, random)
    },
    /*
    * Responses for Specific Text Slide
    */
    async submitSpecificText (player) {
      if (player.name === 'Always Right') { await this.submitCorrectText(player); return }
      await this.submitRandomText(player)
    },
    async submitCorrectText (player, isCorrect) {
      this.$store.commit('setActivePlayer', player)
      const answer = this.slide.answers[0].text
      await this.slide.respondWithSpecificText(answer)
    },
    async submitRandomText (player) {
      this.$store.commit('setActivePlayer', player)
      const text = this.getRandomText()
      await this.slide.respondWithSpecificText(text)
    },
    /*
    * Responses for WordCloud Slide
    */
    async submitWordCloudText (player) {
      this.$store.commit('setActivePlayer', player)
      const text = this.getRandomAnimal()
      await this.slide.respondWithText(text)
    },
    /*
    * Responses for UserVote Slide
    */
    async submitUserVoteText (player) {
      this.$store.commit('setActivePlayer', player)
      const text = this.getRandomText()
      await this.slide.respondWithText(text)
    },
    async submitUserVotes (players) {
      const responses = this.slide.responsesThisSlide
      console.log('sending mock user votes. responses, players: ', responses, players)
      const alwaysRightPlayerId = players.find(player => player.name === 'Always Right').id
      const alwaysRightResponseId = responses.find(response => response.participantId === alwaysRightPlayerId).id

      const filteredResponses = responses.filter((resp) => {
        return this.slide.getPlayerByResponse(resp).name !== 'Wrong Every Time' && this.slide.getPlayerByResponse(resp).name !== 'Always Right'
      })

      // cast 5 votes for "Always Right" and none for "Wrong Every Time"
      for (let i = 0; i < players.length; i++) {
        this.$store.commit('setActivePlayer', players[i])

        if (i < 5) {
          await this.slide.voteForResponse(alwaysRightResponseId, { testing: true })
        } else {
          const int = this.getRandomInt(0, filteredResponses.length - 1)
          await this.slide.voteForResponse(filteredResponses[int].id, { testing: true })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.test-controls {
  position: absolute;
  top:5px;
  left: 5px;

  &.hidden {
    .buttons {
      display: none;
    }

    .toggle {
      opacity: 0;
    }

    &:hover .toggle {
      transition: opacity .2s ease;
      opacity: 1;
    }
  }
}
</style>
